/**
 * Typed table cells: booleans as checkboxes, arrays as chips, dates via
 * dayjs, [[wikilink]]-shaped values as clickable links, and inline editing
 * for plain frontmatter props (string/number/boolean). file.* and tags are
 * read-only.
 */
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { parseFrontmatter, titleFromPath } from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { TAGS_PROP } from './config.js';
import {
  coerceInput,
  formatDate,
  isDateish,
  parseWikilinkValue,
  type BaseRow,
} from './evaluate.js';
import { frontmatterSplice } from './fm.js';

/** Rewrite ONE prop of a note's frontmatter by splicing only the fm block
 * of its Y.Text in a single transact. */
export function writeNoteProp(noteId: string, prop: string, value: unknown): boolean {
  const yt = vault.ytext(noteId);
  const doc = vault.noteDoc(noteId);
  if (!yt || !doc) return false;
  const current = yt.toString();
  const props: Record<string, unknown> = {
    ...(parseFrontmatter(current).data ?? {}),
    [prop]: value,
  };
  const { deleteLen, insert } = frontmatterSplice(current, props);
  doc.transact(() => {
    if (deleteLen > 0) yt.delete(0, deleteLen);
    if (insert) yt.insert(0, insert);
  });
  return true;
}

/** Sync best-effort wikilink resolution: exact path, path + .md, then
 * basename match (shortest path wins) — mirrors obsidian-short resolution. */
export function resolveWikilinkTarget(target: string): string | null {
  const t = target.trim();
  if (!t) return null;
  const byPath = vault.findByPath(t) ?? vault.findByPath(`${t}.md`);
  if (byPath) return byPath;
  const lower = t.toLowerCase();
  let best: { id: string; path: string } | null = null;
  for (const [id, e] of vault.filesSnapshot().entries()) {
    if (e.deleted) continue;
    if (titleFromPath(e.path).toLowerCase() !== lower) continue;
    if (!best || e.path.length < best.path.length) best = { id, path: e.path };
  }
  return best?.id ?? null;
}

/** Editable when it's a plain top-level frontmatter prop whose value is a
 * scalar (or empty). Dotted keys are flattened nested props — read-only. */
const isEditableProp = (prop: string, value: unknown): boolean =>
  prop !== TAGS_PROP &&
  !prop.includes('.') &&
  (value === undefined ||
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean');

function LinkOrText({
  value,
  onOpenNote,
}: {
  value: unknown;
  onOpenNote: (id: string) => void;
}) {
  const wl = parseWikilinkValue(value);
  if (wl) {
    const targetId = resolveWikilinkTarget(wl.target);
    return (
      <button
        type="button"
        className={`base-cell-link${targetId ? '' : ' unresolved'}`}
        onClick={(e) => {
          e.stopPropagation();
          if (targetId) onOpenNote(targetId);
        }}
      >
        {wl.display}
      </button>
    );
  }
  if (isDateish(value)) return <span className="base-cell-date">{formatDate(value)}</span>;
  return <>{value == null ? '' : String(value)}</>;
}

export interface CellProps {
  row: BaseRow;
  prop: string;
  onOpenNote: (id: string) => void;
  /** Optimistic local echo after a successful inline edit. */
  onEdited: (noteId: string, prop: string, value: unknown) => void;
}

export function Cell({ row, prop, onOpenNote, onEdited }: CellProps) {
  const value = row.props[prop];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const committed = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // ---- read-only pseudo/list cells --------------------------------------
  if (prop === TAGS_PROP) {
    return (
      <span className="base-chips">
        {row.tags.map((t) => (
          <span className="base-chip" key={t}>
            #{t}
          </span>
        ))}
      </span>
    );
  }
  if (prop === 'file.ctime') {
    return <span className="base-cell-date">{typeof value === 'number' ? formatDate(value) : ''}</span>;
  }
  if (prop.startsWith('file.')) {
    return <>{value == null ? '' : String(value)}</>;
  }
  if (Array.isArray(value)) {
    return (
      <span className="base-chips">
        {value.map((v, i) => (
          <span className="base-chip" key={i}>
            <LinkOrText value={v} onOpenNote={onOpenNote} />
          </span>
        ))}
      </span>
    );
  }

  const editable = isEditableProp(prop, value);

  // ---- boolean: checkbox, toggle commits immediately --------------------
  if (typeof value === 'boolean') {
    return (
      <input
        type="checkbox"
        className="base-check"
        checked={value}
        disabled={!editable}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const next = e.target.checked;
          if (writeNoteProp(row.id, prop, next)) onEdited(row.id, prop, next);
        }}
      />
    );
  }

  // ---- inline text/number edit ------------------------------------------
  if (editing) {
    const commit = () => {
      if (committed.current) return;
      committed.current = true;
      setEditing(false);
      const next = coerceInput(draft, value);
      if (next !== (value ?? '') && writeNoteProp(row.id, prop, next)) {
        onEdited(row.id, prop, next);
      }
    };
    return (
      <input
        ref={inputRef}
        className="base-cell-input"
        value={draft}
        type={typeof value === 'number' ? 'number' : 'text'}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') {
            committed.current = true;
            setEditing(false);
          }
        }}
      />
    );
  }

  const wl = parseWikilinkValue(value);
  const startEdit = editable && !wl
    ? (e: ReactMouseEvent) => {
        e.stopPropagation();
        committed.current = false;
        setDraft(value == null ? '' : String(value));
        setEditing(true);
      }
    : undefined;

  return (
    <span
      className={`base-cell-value${startEdit ? ' editable' : ''}`}
      onClick={startEdit}
      role={startEdit ? 'button' : undefined}
    >
      <LinkOrText value={value} onOpenNote={onOpenNote} />
      {startEdit && (value == null || value === '') && <span className="base-cell-hint">—</span>}
    </span>
  );
}
